import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import RefreshIcon from '@material-ui/icons/Refresh';
import { setLoadingRedux } from 'reducers/shareData/action';
import { login, postApiAndFunctionName, snackBarRsp } from 'main_api';
import { warningSnack } from 'main_consts';
import { inputChange } from 'main_func';
import { Iobject } from 'main_type';
import Input from 'components/Material/Input';
import background from '../../img/login2-bg.png';
import logoImg from '../../img/logo.png';

// const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CAPTCHA_CHARS = '123456789';
const CAPTCHA_LENGTH = 4;
const CAPTCHA_WIDTH = 120;
const CAPTCHA_HEIGHT = 44;

const rnd = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const generateCaptchaCode = (): string => {
  let code = '';
  for (let i = 0; i < CAPTCHA_LENGTH; i += 1) {
    code += CAPTCHA_CHARS.charAt(rnd(0, CAPTCHA_CHARS.length - 1));
  }
  return code;
};

const drawCaptcha = (canvas: HTMLCanvasElement, code: string) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.clearRect(0, 0, CAPTCHA_WIDTH, CAPTCHA_HEIGHT);
  ctx.fillStyle = '#f0f0f5';
  ctx.fillRect(0, 0, CAPTCHA_WIDTH, CAPTCHA_HEIGHT);

  for (let i = 0; i < 6; i += 1) {
    ctx.strokeStyle = `rgba(${rnd(0, 180)},${rnd(0, 180)},${rnd(0, 180)},0.5)`;
    ctx.beginPath();
    ctx.moveTo(rnd(0, CAPTCHA_WIDTH), rnd(0, CAPTCHA_HEIGHT));
    ctx.lineTo(rnd(0, CAPTCHA_WIDTH), rnd(0, CAPTCHA_HEIGHT));
    ctx.stroke();
  }

  const charWidth = CAPTCHA_WIDTH / code.length;
  code.split('').forEach((ch, i) => {
    ctx.save();
    ctx.translate(charWidth * i + charWidth / 2, CAPTCHA_HEIGHT / 2);
    ctx.rotate((rnd(-25, 25) * Math.PI) / 180);
    ctx.font = 'bold 22px Arial';
    ctx.fillStyle = `rgb(${rnd(0, 100)},${rnd(0, 100)},${rnd(0, 150)})`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(ch, 0, 0);
    ctx.restore();
  });

  for (let i = 0; i < 30; i += 1) {
    ctx.fillStyle = `rgba(${rnd(0, 200)},${rnd(0, 200)},${rnd(0, 200)},0.6)`;
    ctx.beginPath();
    ctx.arc(rnd(0, CAPTCHA_WIDTH), rnd(0, CAPTCHA_HEIGHT), 1, 0, Math.PI * 2);
    ctx.fill();
  }
};

const masterDefaultValue = {
  ban: '',
  account: '',
  password: '',
  captcha: '',
};

const passwordDefaultValue = {
  ban: '',
  id: '',
  oldPwd: '',
  newPwd: '',
  newPwdConfirm: '',
};

/** 登入頁 */
const Login = (): JSX.Element => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [master, setMaster] = useState(masterDefaultValue);
  const [captchaCode, setCaptchaCode] = useState(generateCaptchaCode());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState(passwordDefaultValue);
  const [passwordError, setPasswordError] = useState('');

  const refreshCaptcha = () => {
    setCaptchaCode(generateCaptchaCode());
    setMaster((prev) => ({ ...prev, captcha: '' }));
  };

  useEffect(() => {
    if (canvasRef.current) {
      drawCaptcha(canvasRef.current, captchaCode);
    }
  }, [captchaCode]);

  const updateData = (updateObj: Iobject) => {
    setMaster({ ...master, ...updateObj });
  };

  const updatePasswordData = (
    name: keyof typeof passwordDefaultValue,
    value: string
  ) => {
    setPasswordForm((current) => ({ ...current, [name]: value }));
    setPasswordError('');
  };

  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordForm(passwordDefaultValue);
    setPasswordError('');
  };

  const handlePasswordSubmit = async () => {
    if (passwordForm.newPwd !== passwordForm.newPwdConfirm) {
      setPasswordError('新密碼與新密碼確認不一致。');
      return;
    }

    dispatch(setLoadingRedux(true));
    try {
      const result = await postApiAndFunctionName(
        'login',
        'changePwd',
        passwordForm
      );
      if (snackBarRsp(enqueueSnackbar, result) && result.data.status === 0) {
        closePasswordDialog();
        window.location.href = '/login';
      }
    } catch (error) {
      setPasswordError('修改密碼失敗，請稍後再試。');
    } finally {
      dispatch(setLoadingRedux(false));
    }
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!master.ban || !master.account || !master.password || !master.captcha) {
      return;
    }
    if (master.captcha.trim().toUpperCase() !== captchaCode) {
      enqueueSnackbar('驗證碼錯誤，請重新輸入', warningSnack);
      refreshCaptcha();
      return;
    }

    dispatch(setLoadingRedux(true));
    const userId = `N_${master.ban}_${master.account}`;
    const result = await login(userId, master.password);
    const { data: dataTemp } = result.data;
    const { isContinue } = (dataTemp as { isContinue?: string }) || {};
    dispatch(setLoadingRedux(false));
    if (result.data.status === 0) {
      window.location.href = '/';
      return;
    }
    if (isContinue === 'Y') {
      const confirmMsg = [
        '是否沿用目前密碼？\n\n',
        '點擊「確定」沿用密碼，',
        '點擊「取消」前往變更密碼。',
      ].join('\n\n');
      const checkContinue = window.confirm(confirmMsg);
      if (checkContinue) {
        await postApiAndFunctionName('login', 'isContinue', {
          userId,
          password: master.password,
        });
        // 沿用密碼
        window.location.href = '/';
      } else {
        setPasswordForm({
          ...passwordDefaultValue,
          ban: master.ban,
          id: master.account,
          oldPwd: master.password,
        });
        setPasswordDialogOpen(true);
      }
    } else {
      snackBarRsp(enqueueSnackbar, result);
      refreshCaptcha();
    }
  };
  return (
    <div
      id="page-area"
      style={{
        backgroundImage: `url(${background})`,
        height: '100vh',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        elevation={3}
        style={{
          width: '360px',
          padding: '2.5em 2em',
          textAlign: 'center',
        }}
      >
        <div style={{ marginBottom: '1.0em' }}>
          <img src={logoImg} alt="Logo" style={{ height: '50px' }} />
        </div>
        <div
          style={{
            fontSize: '2.0rem',
            fontWeight: 900,
            color: '#000000',
            marginBottom: '1.5em',
          }}
        >
          快遞業界整合資訊平台
        </div>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Input
                InputLabelProps={{
                  style: { fontSize: '1.4rem' },
                }}
                required
                label="統一編號"
                name="ban"
                autoFocus
                value={master.ban}
                inputProps={{
                  maxLength: 8,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                onChange={(e) => {
                  inputChange(updateData)(e);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                InputLabelProps={{
                  style: { fontSize: '1.4rem' },
                }}
                required
                label="帳號"
                name="account"
                value={master.account}
                onChange={(e) => {
                  inputChange(updateData)(e);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                InputLabelProps={{
                  style: { fontSize: '1.4rem' },
                }}
                required
                label="密碼"
                name="password"
                type="password"
                value={master.password}
                onChange={(e) => {
                  inputChange(updateData)(e);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1} alignItems="center" wrap="nowrap">
                <Grid item xs>
                  <Input
                    InputLabelProps={{
                      style: { fontSize: '1.4rem' },
                    }}
                    required
                    label="驗證碼"
                    name="captcha"
                    autoComplete="off"
                    inputProps={{ maxLength: CAPTCHA_LENGTH }}
                    value={master.captcha}
                    onChange={(e) => {
                      inputChange(updateData)(e);
                    }}
                  />
                </Grid>
                <Grid item>
                  <canvas
                    ref={canvasRef}
                    width={CAPTCHA_WIDTH}
                    height={CAPTCHA_HEIGHT}
                    style={{
                      verticalAlign: 'middle',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    onClick={refreshCaptcha}
                    title="看不清楚？點擊更換"
                  />
                </Grid>
                <Grid item>
                  <Button
                    type="button"
                    size="small"
                    onClick={refreshCaptcha}
                    style={{ minWidth: 'auto', padding: '4px' }}
                  >
                    <RefreshIcon fontSize="small" />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                style={{ fontWeight: 800, fontSize: '1.1rem' }}
              >
                登入
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Dialog
        open={passwordDialogOpen}
        onClose={closePasswordDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>修改密碼</DialogTitle>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handlePasswordSubmit();
          }}
        >
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Input
                  required
                  fullWidth
                  type="password"
                  label="舊密碼"
                  name="oldPwd"
                  value={passwordForm.oldPwd}
                  onChange={(event) =>
                    updatePasswordData('oldPwd', event.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Input
                  required
                  fullWidth
                  type="password"
                  label="新密碼"
                  name="newPwd"
                  value={passwordForm.newPwd}
                  onChange={(event) =>
                    updatePasswordData('newPwd', event.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Input
                  required
                  fullWidth
                  type="password"
                  label="確認密碼"
                  name="newPwdConfirm"
                  value={passwordForm.newPwdConfirm}
                  onChange={(event) =>
                    updatePasswordData('newPwdConfirm', event.target.value)
                  }
                />
              </Grid>
            </Grid>
            {passwordError && (
              <div style={{ color: 'red', marginTop: '1em' }}>
                {passwordError}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={closePasswordDialog}>
              取消
            </Button>
            <Button type="submit" variant="contained" color="primary">
              確定
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default Login;
